import React, { useCallback, useState, createContext, useContext } from 'react';
import { CheckCircle, AlertCircle, Info, X } from 'lucide-react';
export type ToastType = 'success' | 'error' | 'info';
export interface Toast {
  id: string;
  message: string;
  type: ToastType;
}
interface ToastContextType {
  showToast: (message: string, type?: ToastType) => void;
  success: (message: string) => void;
  error: (message: string) => void;
  info: (message: string) => void;
}
const ToastContext = createContext<ToastContextType | undefined>(undefined);
export function ToastProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const removeToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);
  const showToast = useCallback((message: string, type: ToastType = 'info') => {
    const id = Math.random().toString(36).substring(2, 9);
    setToasts(prev => [...prev, {
      id,
      message,
      type
    }]);
    setTimeout(() => {
      removeToast(id);
    }, 4000);
  }, [removeToast]);
  const success = useCallback((message: string) => showToast(message, 'success'), [showToast]);
  const error = useCallback((message: string) => showToast(message, 'error'), [showToast]);
  const info = useCallback((message: string) => showToast(message, 'info'), [showToast]);
  return <ToastContext.Provider value={{
    showToast,
    success,
    error,
    info
  }} data-id="element-18366">
      {children}
      <div className="fixed bottom-4 right-4 z-50 flex flex-col gap-2" data-id="element-18367">
        {toasts.map(toast => <ToastItem key={toast.id} toast={toast} onClose={() => removeToast(toast.id)} data-id="element-18368" />)}
      </div>
    </ToastContext.Provider>;
}
function ToastItem({
  toast,
  onClose
}: {
  toast: Toast;
  onClose: () => void;
}) {
  const icons = {
    success: <CheckCircle className="w-5 h-5 text-emerald-500" data-id="element-18369" />,
    error: <AlertCircle className="w-5 h-5 text-rose-500" data-id="element-18370" />,
    info: <Info className="w-5 h-5 text-blue-500" data-id="element-18371" />
  };
  const bgColors = {
    success: 'bg-emerald-50 border-emerald-200',
    error: 'bg-rose-50 border-rose-200',
    info: 'bg-blue-50 border-blue-200'
  };
  return <div className={`flex items-center gap-3 px-4 py-3 rounded-xl shadow-lg border ${bgColors[toast.type]} min-w-[300px] max-w-md animate-in slide-in-from-bottom-5 fade-in duration-300`} data-id="element-18372">
      {icons[toast.type]}
      <p className="text-sm font-medium text-slate-800 flex-1" data-id="element-18373">
        {toast.message}
      </p>
      <button onClick={onClose} className="p-1 text-slate-400 hover:text-slate-600 rounded-full hover:bg-slate-100/50 transition-colors" data-id="element-18374">
        <X className="w-4 h-4" data-id="element-18375" />
      </button>
    </div>;
}
export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
}